import { useCallback, useEffect, useState } from 'react';

/**
 * Desvanecido en los bordes de una zona scrolleable, para que se vea que hay
 * más contenido arriba o abajo del recorte.
 *
 * Se resuelve con `mask-image` sobre el propio contenedor: un overlay con
 * gradiente necesitaría conocer el color de fondo, que cambia entre la vista
 * Terrio y las compartidas branded.
 */

const DEFAULT_FADE_SIZE = 24;
/** Margen para no encender el fade por un residuo de scroll de subpíxel. */
const EDGE_THRESHOLD = 8;

const NO_EDGES = { top: false, bottom: false, left: false, right: false };

/**
 * `scrollRef` es un callback ref (no un objeto): el contenedor puede montarse
 * después del primer render (paneles detrás de un loading) y así el hook se
 * suscribe recién cuando el nodo existe.
 *
 * `axis: 'x'` mide overflow horizontal (chips que se deslizan). El default
 * sigue siendo vertical.
 *
 * @param {{ axis?: 'x' | 'y' }} [options]
 * @returns {{
 *   scrollRef: (node: HTMLElement|null) => void,
 *   fadeTop: boolean,
 *   fadeBottom: boolean,
 *   fadeLeft: boolean,
 *   fadeRight: boolean,
 *   refreshScrollEdges: () => void,
 * }}
 */
export function useScrollEdgeFade({ axis = 'y' } = {}) {
  const [node, setNode] = useState(null);
  const [edges, setEdges] = useState(NO_EDGES);

  const measure = useCallback((el) => {
    if (!el) {
      setEdges((prev) => (
        prev.top || prev.bottom || prev.left || prev.right ? NO_EDGES : prev
      ));
      return;
    }
    if (axis === 'x') {
      const left = el.scrollLeft > EDGE_THRESHOLD;
      const right = el.scrollWidth - el.scrollLeft - el.clientWidth > EDGE_THRESHOLD;
      setEdges((prev) => (
        prev.left === left && prev.right === right ? prev : { ...NO_EDGES, left, right }
      ));
      return;
    }
    const top = el.scrollTop > EDGE_THRESHOLD;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight > EDGE_THRESHOLD;
    setEdges((prev) => (
      prev.top === top && prev.bottom === bottom ? prev : { ...NO_EDGES, top, bottom }
    ));
  }, [axis]);

  const refreshScrollEdges = useCallback(() => measure(node), [measure, node]);

  useEffect(() => {
    if (!node) {
      measure(null);
      return undefined;
    }

    const update = () => measure(node);
    const rafId = window.requestAnimationFrame(update);
    node.addEventListener('scroll', update, { passive: true });

    // El contenido crece por scroll infinito, filtros o secciones que aparecen
    // (avisos, resultados en línea): se observan todos los hijos directos, no
    // solo el primero, y los que se agregan después.
    let observer;
    let mutations;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(node);
      const observeChildren = () => {
        Array.from(node.children).forEach((child) => observer.observe(child));
      };
      observeChildren();
      if (typeof MutationObserver !== 'undefined') {
        mutations = new MutationObserver(() => {
          observeChildren();
          update();
        });
        mutations.observe(node, { childList: true });
      }
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      node.removeEventListener('scroll', update);
      observer?.disconnect();
      mutations?.disconnect();
    };
  }, [node, measure]);

  return {
    scrollRef: setNode,
    fadeTop: edges.top,
    fadeBottom: edges.bottom,
    fadeLeft: edges.left,
    fadeRight: edges.right,
    refreshScrollEdges,
  };
}

/**
 * Estilos de máscara para la zona scrolleable.
 * @param {boolean} fadeTop
 * @param {boolean} fadeBottom
 * @param {number} [size] Alto del degradado en px.
 * @returns {Object} sx parcial (sin máscara si no hay bordes que desvanecer).
 */
export function getScrollEdgeFadeSx(fadeTop, fadeBottom, size = DEFAULT_FADE_SIZE) {
  if (!fadeTop && !fadeBottom) return {};

  const mask = `linear-gradient(to bottom, transparent 0, #000 ${
    fadeTop ? size : 0
  }px, #000 calc(100% - ${fadeBottom ? size : 0}px), transparent 100%)`;

  return { maskImage: mask, WebkitMaskImage: mask };
}

/**
 * Misma máscara, en horizontal: el borde izquierdo y/o derecho se desvanece.
 * @param {boolean} fadeLeft
 * @param {boolean} fadeRight
 * @param {number} [size]
 * @returns {Object}
 */
export function getScrollEdgeFadeHorizontalSx(fadeLeft, fadeRight, size = DEFAULT_FADE_SIZE) {
  if (!fadeLeft && !fadeRight) return {};

  const mask = `linear-gradient(to right, transparent 0, #000 ${
    fadeLeft ? size : 0
  }px, #000 calc(100% - ${fadeRight ? size : 0}px), transparent 100%)`;

  return { maskImage: mask, WebkitMaskImage: mask };
}
